import { BellRing, Check, CircleCheck, Webhook } from "lucide-react";
import { linePath, smoothPoints, type Pt } from "features/onboarding/art/chart";
import {
  Art,
  ArtSwitch,
  Card,
  Chip,
  Head,
  Line,
  Toast,
  Window,
} from "features/onboarding/art/live";
import {
  TXT,
  draw,
  fade,
  follow,
  font,
  r2,
  ride,
  rise,
  soft,
  tone,
  useArtId,
  type Box,
  type Scene,
} from "features/onboarding/art/kit";
import useArtLabels from "features/onboarding/art/labels";

// Ported from cornix.io (feature/art/tradingview-bots.tsx): an alert fires on
// a moving-average crossover, the webhook reaches the bot, and the bot places
// the order with your entry, size, take-profit and stop-loss.

const PRICE: Pt[] = [
  [0, 0.36],
  [0.3, 0.72],
  [0.62, 0.42],
  [1, 0.12],
];
const MA: Pt[] = [
  [0, 0.3],
  [0.2, 0.42],
  [0.4, 0.53],
  [0.6, 0.51],
  [0.8, 0.43],
  [1, 0.33],
];

type Geo = {
  name: string;
  w: number;
  h: number;
  card: Box;
  plot: Box;
  link: string;
  pill: Pt;
  win: Box;
  rowY: number;
  rowH: number;
  toast: { x: number; y: number } | null;
};

const WIDE: Geo = {
  name: "w",
  w: 640,
  h: 440,
  card: { x: 14, y: 26, w: 300, h: 218 },
  plot: { x: 32, y: 84, w: 262, h: 140 },
  link: "M226,244 C226,290 262,300 300,300",
  pill: [252, 282],
  win: { x: 300, y: 118, w: 326, h: 272 },
  rowY: 214,
  rowH: 44,
  toast: { x: 34, y: 330 },
};

const TALL: Geo = {
  name: "t",
  w: 360,
  h: 482,
  card: { x: 20, y: 10, w: 320, h: 196 },
  plot: { x: 38, y: 66, w: 284, h: 124 },
  link: "M180,206 L180,240",
  pill: [228, 223],
  win: { x: 8, y: 240, w: 344, h: 232 },
  rowY: 332,
  rowH: 36,
  toast: null,
};

export function TradingViewBotsArt({ play }: { play?: boolean }) {
  return (
    <ArtSwitch
      wide={<TradingViewBots g={WIDE} play={play} />}
      narrow={<TradingViewBots g={TALL} play={play} />}
    />
  );
}

function TradingViewBots({ g, play }: { g: Geo; play?: boolean }) {
  const L = useArtLabels();
  const id = useArtId(`tv-${g.name}`);
  const { plot, win } = g;
  const map = (pts: Pt[]) =>
    smoothPoints(
      pts.map(([u, v]): Pt => [plot.x + u * plot.w, plot.y + v * plot.h]),
      10
    ).map(([x, y]): Pt => [r2(x), r2(y)]);
  const price = map(PRICE);
  const ma = map(MA);
  const maAt = (x: number) => {
    const i = Math.max(
      1,
      ma.findIndex(([mx]) => mx >= x)
    );
    const [ax, ay] = ma[i - 1];
    const [bx, by] = ma[i];
    return ay + ((by - ay) * (x - ax)) / (bx - ax || 1);
  };
  const low = price.reduce((b, p, i) => (p[1] > price[b][1] ? i : b), 0);
  const crossI = price.findIndex(([x, y], i) => i > low && y < maAt(x));
  const alert = price[crossI];
  const fAlert = follow(price).fractions[crossI];
  const end = price[price.length - 1];

  const rows = [
    { label: L.entry, value: `${L.market} · 64,420` },
    { label: L.size, value: "10%" },
    { label: L.takeProfit, value: "+3% · 66,350" },
    { label: L.stopLoss, value: "−1.5% · 63,450" },
  ];

  const scene: Scene = (tl, $) => {
    rise(tl, $("ma"), 0.2, 0, 0.6);
    ride(tl, $("price"), $("head")[0], price, 0.3, 1.8, 0, fAlert);
    const fired = 2.1;
    tl.fromTo(
      $("flag"),
      { opacity: 0, y: 8 },
      { opacity: 1, y: 0, duration: 0.5, ease: "back.out(2.4)" },
      fired
    );
    tl.fromTo(
      $("bell"),
      { rotation: -18, svgOrigin: `${alert[0]} ${alert[1] - 60}` },
      { rotation: 0, duration: 0.9, ease: "elastic.out(1.2, 0.3)" },
      fired + 0.1
    );
    rise(tl, $("alert-chip"), fired + 0.2, 6, 0.5);
    draw(tl, $("wire"), fired + 0.4, 0.6, "power2.inOut");
    rise(tl, $("pill"), fired + 0.5, 4, 0.4);
    rise(tl, $("received"), fired + 1, 6, 0.5);
    $("row*").forEach((el, i) => rise(tl, el, fired + 1.2 + i * 0.25, 6, 0.5));
    $("check*").forEach((el, i) =>
      tl.fromTo(
        el,
        { attr: { r: 0 } },
        { attr: { r: 9 }, duration: 0.45, ease: "back.out(2.6)" },
        fired + 1.35 + i * 0.25
      )
    );
    ride(tl, $("price"), $("head")[0], price, fired + 1.4, 1.6, fAlert, 1);
    if (g.toast) rise(tl, $("toast"), fired + 2.6, 12, 0.7);
    else {
      fade(tl, $("received"), fired + 2.5, 0.25);
      rise(tl, $("filled"), fired + 2.7, 6, 0.5);
    }
    const rest = fired + 3.4;

    const out = rest + 3.6;
    fade(
      tl,
      [
        ...$("price"),
        ...$("head"),
        ...$("ma"),
        ...$("flag"),
        ...$("alert-chip"),
        ...$("wire"),
        ...$("pill"),
        ...$("received"),
        ...$("row*"),
        ...$(g.toast ? "toast" : "filled"),
      ],
      out
    );
    tl.to({}, { duration: 0.6 }, out + 0.5);
    return rest;
  };

  const chipW = (text: string, min: number) =>
    Math.max(min, Math.round(text.length * 7 + 24));
  const alertW = chipW(L.entryAlert, 96);
  const receivedW = chipW(L.received, 96);
  const filledText = L.filled;
  const filledW = chipW(filledText, 124);

  return (
    <Art id={id} w={g.w} h={g.h} scene={scene} play={play}>
      {/* TradingView chart with the alert condition */}
      <Card id={id} x={g.card.x} y={g.card.y} w={g.card.w} h={g.card.h}>
        <text
          x={g.card.x + 18}
          y={g.card.y + 28}
          className={TXT.ink}
          style={font(13, 600)}
        >
          TradingView
        </text>
        <text x={g.card.x + 110} y={g.card.y + 28} className={TXT.label}>
          BTCUSDT · 1h
        </text>
        <line
          x1={g.card.x}
          x2={g.card.x + g.card.w}
          y1={g.card.y + 44}
          y2={g.card.y + 44}
          stroke="var(--viz-card-line)"
        />
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1={plot.x}
            x2={plot.x + plot.w}
            y1={plot.y + f * plot.h}
            y2={plot.y + f * plot.h}
            stroke="var(--viz-rule-soft)"
          />
        ))}
        <path
          data-k="ma"
          d={linePath(ma)}
          fill="none"
          stroke={tone("gold")}
          strokeWidth={1.8}
          strokeDasharray="5 4"
          strokeLinecap="round"
        />
        <Line k="price" d={linePath(price)} w={2.2} />
        <Head k="head" x={end[0]} y={end[1]} />
        <g data-k="flag">
          <line
            x1={alert[0]}
            x2={alert[0]}
            y1={alert[1] - 8}
            y2={alert[1] - 30}
            stroke={tone("gold")}
            strokeWidth={2}
          />
          <circle
            cx={alert[0]}
            cy={alert[1]}
            r={6}
            fill="var(--viz-card)"
            stroke={tone("gold")}
            strokeWidth={2.5}
          />
          <g data-k="bell">
            <rect
              x={alert[0] - 17}
              y={alert[1] - 64}
              width={34}
              height={34}
              rx={10}
              fill="var(--viz-card)"
            />
            <rect
              x={alert[0] - 17}
              y={alert[1] - 64}
              width={34}
              height={34}
              rx={10}
              fill={soft("gold")}
              stroke={tone("gold")}
              strokeWidth={1.5}
            />
            <BellRing
              x={alert[0] - 9}
              y={alert[1] - 56}
              width={18}
              height={18}
              color={tone("gold")}
              strokeWidth={2}
            />
          </g>
        </g>
        <Chip
          k="alert-chip"
          x={g.card.x + g.card.w - 10 - alertW / 2}
          y={g.card.y + 23}
          text={L.entryAlert}
          t="gold"
          w={alertW}
        />
      </Card>

      {/* Webhook */}
      <path
        data-k="wire"
        d={g.link}
        pathLength={1}
        fill="none"
        stroke="var(--viz-accent)"
        strokeWidth={2}
        strokeDasharray="1 1"
        strokeDashoffset={0}
        strokeLinecap="round"
      />
      <g data-k="pill">
        <rect
          x={g.pill[0] - 16}
          y={g.pill[1] - 12}
          width={32}
          height={24}
          rx={12}
          fill="var(--viz-card)"
          stroke="var(--viz-accent-line)"
        />
        <Webhook
          x={g.pill[0] - 7}
          y={g.pill[1] - 7}
          width={14}
          height={14}
          color={tone("accent")}
          strokeWidth={2}
        />
      </g>

      {/* The Cornix bot applies your settings */}
      <Window
        id={id}
        x={win.x}
        y={win.y}
        w={win.w}
        h={win.h}
        title={L.tradingViewBot}
      >
        <g data-in="0.1">
          <text x={win.x + 20} y={win.y + 66} className={TXT.label}>
            BTC/USDT · {L.long}
          </text>
        </g>
        {g.toast ? (
          <Chip
            k="received"
            x={win.x + win.w - 18 - receivedW / 2}
            y={win.y + 62}
            text={L.received}
            t="accent"
            w={receivedW}
          />
        ) : (
          <>
            <g data-k="received" opacity={0}>
              <Chip
                x={win.x + win.w - 18 - receivedW / 2}
                y={win.y + 62}
                text={L.received}
                t="accent"
                w={receivedW}
              />
            </g>
            <Chip
              k="filled"
              x={win.x + win.w - 18 - filledW / 2}
              y={win.y + 62}
              text={filledText}
              t="up"
              w={filledW}
              solid
            />
          </>
        )}
        {rows.map((r, i) => {
          const y = g.rowY + i * g.rowH;
          return (
            <g key={i}>
              {i ? (
                <line
                  data-in="0.15"
                  x1={win.x + 16}
                  x2={win.x + win.w - 16}
                  y1={y - g.rowH / 2}
                  y2={y - g.rowH / 2}
                  stroke="var(--viz-rule-soft)"
                />
              ) : null}
              <g data-k={`row${i}`}>
                <text
                  x={win.x + 48}
                  y={y + 4}
                  className={TXT.ink2}
                  style={font(12.5, 500)}
                >
                  {r.label}
                </text>
                <text
                  x={win.x + win.w - 18}
                  y={y + 4}
                  textAnchor="end"
                  className={`${TXT.ink} ${TXT.num}`}
                  style={font(11.5, 600)}
                >
                  {r.value}
                </text>
                <circle
                  data-k={`check${i}`}
                  cx={win.x + 28}
                  cy={y}
                  r={9}
                  fill={soft("up")}
                />
                <Check
                  x={win.x + 22}
                  y={y - 6}
                  width={12}
                  height={12}
                  color={tone("up")}
                  strokeWidth={3}
                />
              </g>
            </g>
          );
        })}
      </Window>
      {g.toast ? (
        <Toast
          id={id}
          k="toast"
          x={g.toast.x}
          y={g.toast.y}
          icon={CircleCheck}
          title={L.entryFilled}
          meta={`${L.buy} · BTC/USDT · 10%`}
          t="up"
        />
      ) : null}
    </Art>
  );
}
